import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CaretLeftIcon,
  CellSignalFullIcon,
  WifiHighIcon,
  BatteryFullIcon,
  PaperclipIcon,
  ArrowBendUpLeftIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/cn';

export interface PhonePreviewProps {
  channel: 'sms' | 'email';
  /** SMS sender ID (max 11 chars) or email "From" name. */
  sender: string;
  body: string;
  subject?: string;
  /** Email address shown under the sender name. */
  fromAddress?: string;
  /** Time label on the message, e.g. "10:24". */
  time?: string;
  /** Extra footer under the device, e.g. SMS segment counter. */
  caption?: ReactNode;
  /** Render links/CTA inside the email body. */
  emailCta?: { label: string };
  className?: string;
  /** Visual scale of the device (1 = 300px wide). */
  scale?: number;
}

/** Turn bare URLs into styled spans so previews look like real messages. */
function linkify(text: string, linkCls: string) {
  const parts = text.split(/(https?:\/\/\S+|\b[\w.-]+\.(?:gr|com|link|ly)\/\S*)/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <span key={i} className={linkCls}>
        {p}
      </span>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}

/**
 * A believable phone (always light screen, like most customers' phones) that previews an SMS thread
 * or an email. Used by the message composer and review-request settings.
 *
 * A3 frame: the deepest field blue (`field-deep`, the same in light and dark mode) with a tonal
 * edge, a white screen, links and the email button in the constant field blues. The screen uses
 * only white/black and the theme-invariant field tokens, so it stays a light phone in dark mode.
 */
export function PhonePreview({
  channel,
  sender,
  body,
  subject,
  fromAddress,
  time = '10:24',
  caption,
  emailCta,
  className,
  scale = 1,
}: PhonePreviewProps) {
  const { t } = useTranslation('common');
  return (
    <figure className={cn('inline-flex flex-col items-center gap-3', className)}>
      <div
        className="relative rounded-[46px] bg-field-deep p-[10px] shadow-[0_30px_60px_-30px_rgb(4_18_34/0.55),inset_0_0_0_1.5px_var(--gh-field-tonal),0_0_0_1px_rgb(4_18_34/0.6)]"
        style={{ width: 300 * scale }}
        aria-label={t(channel === 'sms' ? 'phonePreview.smsLabel' : 'phonePreview.emailLabel')}
        role="img"
      >
        <div
          className="relative flex aspect-[9/19] flex-col overflow-hidden rounded-[37px] bg-white font-sans text-black/90"
          style={{ fontSize: 13 * scale }}
        >
          {/* status bar */}
          <div className="relative flex h-11 shrink-0 items-center justify-between px-7 pt-1 text-[0.85em] font-semibold">
            <span>{time}</span>
            <span className="absolute left-1/2 top-2.5 h-[26px] w-[92px] -translate-x-1/2 rounded-full bg-field-deep" />
            <span className="flex items-center gap-1">
              <CellSignalFullIcon weight="fill" className="size-[1.1em]" />
              <WifiHighIcon weight="bold" className="size-[1.1em]" />
              <BatteryFullIcon weight="fill" className="size-[1.3em]" />
            </span>
          </div>

          {channel === 'sms' ? (
            <>
              <div className="flex shrink-0 flex-col items-center gap-1 border-b border-black/5 bg-black/[0.03] pb-2.5 pt-1">
                <CaretLeftIcon
                  weight="bold"
                  className="absolute left-4 top-[3.4em] size-[1.3em] text-field-tonal-strong"
                />
                <span className="inline-flex size-[2.6em] items-center justify-center rounded-full bg-field-tonal text-[0.95em] font-bold text-white">
                  {sender.slice(0, 1).toUpperCase()}
                </span>
                <span className="text-[0.82em] font-medium">{sender || '–'}</span>
              </div>
              <div className="flex flex-1 flex-col gap-1 overflow-hidden px-3 pt-3">
                <div className="mb-1 text-center text-[0.72em] text-black/40">
                  {t('phonePreview.today', { time })}
                </div>
                <div className="max-w-[82%] whitespace-pre-wrap break-words rounded-[1.25em] rounded-bl-[0.4em] bg-black/[0.07] px-[0.95em] py-[0.6em] text-[0.95em] leading-[1.35]">
                  {body ? (
                    linkify(
                      body,
                      'text-field-tonal-strong underline decoration-field-tonal-strong/40',
                    )
                  ) : (
                    <span className="text-black/35">…</span>
                  )}
                </div>
              </div>
              <div className="m-3 flex h-[2.5em] shrink-0 items-center rounded-full border border-black/10 px-3 text-[0.85em] text-black/30">
                {t('phonePreview.messagePlaceholder')}
              </div>
            </>
          ) : (
            <>
              <div className="flex shrink-0 items-center justify-between px-4 pb-2 text-field-tonal-strong">
                <CaretLeftIcon weight="bold" className="size-[1.3em]" />
                <span className="flex gap-3">
                  <ArrowBendUpLeftIcon className="size-[1.2em]" />
                  <PaperclipIcon className="size-[1.2em]" />
                </span>
              </div>
              <div className="flex-1 overflow-hidden px-4">
                <div className="text-[1.25em] font-extrabold leading-tight tracking-headline">
                  {subject || '–'}
                </div>
                <div className="mt-3 flex items-center gap-2.5 border-b border-black/5 pb-3">
                  <span className="inline-flex size-[2.4em] items-center justify-center rounded-full bg-field text-[0.9em] font-bold text-white">
                    {sender.slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0 leading-tight">
                    <div className="truncate text-[0.9em] font-semibold">{sender}</div>
                    <div className="truncate text-[0.78em] text-black/45">
                      {fromAddress ?? t('phonePreview.toMe')}
                    </div>
                  </div>
                  <span className="ml-auto text-[0.75em] text-black/40">{time}</span>
                </div>
                <div className="mt-3 whitespace-pre-wrap break-words text-[0.92em] leading-[1.5] text-black/80">
                  {body ? (
                    linkify(body, 'text-field-tonal-strong underline')
                  ) : (
                    <span className="text-black/35">…</span>
                  )}
                </div>
                {emailCta && (
                  <div className="mt-4 inline-flex rounded-[0.5em] bg-field px-[1.1em] py-[0.6em] text-[0.88em] font-bold text-white">
                    {emailCta.label}
                  </div>
                )}
              </div>
            </>
          )}
          <div className="mx-auto mb-2 mt-1 h-[5px] w-[38%] shrink-0 rounded-full bg-black/80" />
        </div>
      </div>
      {caption && <figcaption className="text-center text-sm text-ink-muted">{caption}</figcaption>}
    </figure>
  );
}
